import React, { useState, useEffect } from 'react';
import styles from './GoAuraConsentToggle.module.css';
import { useUserContext } from '@/contexts/UserContext';
import { updateGoAuraIntegration } from '@/api/settings';
import { useErrorModalContext } from '@/contexts/ErrorModalContext';

const GoAuraConsentToggle = () => {
  const { user, setUser } = useUserContext();
  const [isActive, setIsActive] = useState(user?.goAuraIntegration || false);
  const [isLoading, setIsLoading] = useState(false);
  const { setErrors } = useErrorModalContext();

  // Sync component state with user data when it changes
  useEffect(() => {
    if (user) {
      setIsActive(user.goAuraIntegration || false);
    }
  }, [user]);

  const handleToggle = async () => {
    if (isLoading) return;
    
    try {
      setIsLoading(true);
      const newStatus = !isActive;
      
      // Optimistically update UI
      setIsActive(newStatus);
      
      await updateGoAuraIntegration({ goAuraIntegration: newStatus });
      setUser({ ...user, goAuraIntegration: newStatus }, false);
    } catch (error) {
      // Revert UI state if API call fails
      setIsActive(isActive);
      console.error('Error updating GoAura integration:', error);
      setErrors([{ 
        message: "Failed to update GoAura integration. Please try again later."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.logoSection}>
        <div className={styles.logoContainer}>
          <svg xmlns="http://www.w3.org/2000/svg" width="97" height="20" viewBox="0 0 97 20" fill="none" className={styles.logo}>
              <g clipPath="url(#clip0_11_1124)">
                  <path d="M33.171 3.17969L25.6349 16.466H28.9396L34.8936 5.96721L40.8476 16.466H44.1523L36.6161 3.1797L33.171 3.17969Z" fill="var(--static-text-strong)"/>
                  <path d="M86.0189 3.17969L78.4841 16.466H81.7874L87.7428 5.96721L93.6968 16.466H97L89.4653 3.1797L86.0189 3.17969Z" fill="var(--static-text-strong)"/>
                  <path d="M56.3818 3.11182V9.68487C56.3818 12.097 54.4048 14.0524 51.9658 14.0524C49.5269 14.0524 47.5499 12.097 47.5499 9.68487V3.11182H44.669V9.68487C44.669 13.6709 47.937 16.9032 51.9673 16.9032C55.9977 16.9032 59.2657 13.6709 59.2657 9.68487V3.11182H56.3848H56.3818Z" fill="var(--static-text-strong)"/>
                  <path d="M77.4462 7.70386C77.4462 5.15606 75.3564 3.09217 72.7803 3.09669L62.4394 3.11176V16.466H65.3203V12.3126H71.4754L73.832 16.466H77.1352L74.5789 11.9583C76.2618 11.2633 77.4447 9.62153 77.4447 7.70386H77.4462ZM65.3218 5.9611H72.7954C73.7726 5.9611 74.5652 6.74503 74.5652 7.71141C74.5652 8.67777 73.7726 9.46171 72.7954 9.46171H65.3218V5.9611Z" fill="var(--static-text-strong)"/>
                  <path d="M19.9722 0.5H0V3.09721H19.9722V0.5Z" fill="var(--static-text-strong)"/>
                  <path d="M0 8.23106V10.9584L7.14864 8.66974C8.07751 8.37326 9.03219 8.22351 9.98686 8.22351C10.9415 8.22351 11.8962 8.37175 12.825 8.66974L19.9737 10.9584V8.23106L13.6219 6.19809C11.2436 5.43723 8.73012 5.43723 6.35181 6.19809L0 8.23106Z" fill="var(--static-text-strong)"/>
                  <path d="M19.9737 16.3711L13.6585 12.1532C12.5512 11.4132 11.2704 11.0451 9.9885 11.0454C8.70656 11.0451 7.42575 11.4132 6.31851 12.1532L0 16.3733V19.4999L7.7695 14.3104C8.43883 13.8626 9.21286 13.6388 9.9885 13.6388C10.7641 13.6388 11.5382 13.8626 12.2075 14.3104L19.9737 19.4976V16.3711Z" fill="var(--static-text-strong)"/>
              </g>
              <defs>
                  <clipPath id="clip0_11_1124">
                  <rect width="97" height="19" fill="white" transform="translate(0 0.5)"/>
                  </clipPath>
              </defs>
          </svg>
        </div>
        <div className={`${styles.statusBadge} ${isActive ? styles.statusActive : ''}`}>
          {isActive ? 'Active' : 'Not Active'}
        </div>
      </div>

      <div className={styles.contentSection}>
        <div className={styles.consentText}>
          I Allow the integration of my Boxem account with GoAura.
        </div>
        <div className={styles.dataSection}>
          <div className={styles.dataLabel}>Data Transferred:</div>
          <div className={styles.dataItem}>
            <div className={styles.dataDot}></div>
            <a href="#" className={styles.dataLink}>Buy Cost Data</a>
          </div>
        </div>
      </div>

      <div className={styles.toggleSection}>
        <div className={styles.toggleLabel}>Allow Integration</div>
        <button 
            onClick={handleToggle}
            disabled={isLoading}
            className={`${styles.toggleButton} ${isActive ? styles.toggleActive : ''}`}
            aria-label={isActive ? 'Disable GoAura integration' : 'Enable GoAura integration'}
            aria-busy={isLoading}
        >
            <span className={`${styles.toggleThumb} ${isActive ? styles.toggleThumbActive : ''}`} />
        </button>
        </div>
    </div>
  );
};

export default GoAuraConsentToggle;